import { createBackendRegistry } from "@infrastructure/providers/backendRegistry";
import logger from "@logger";
import { withOperationTimeout } from "@shared/utils/withOperationTimeout";

const { storage } = createBackendRegistry();

const UPLOAD_TIMEOUT_MS = 180_000;
const UPLOAD_TIMEOUT_MESSAGE = "Upload timed out. Check your connection and tap Retry.";

/**
 * Photo upload service for managing profile photo uploads and deletions.
 *
 * Handles uploading and deleting profile photos using Firebase Storage
 * with proper error handling, logging, and unique filename generation.
 *
 * @example
 * ```typescript
 * import photoUploadService from '@modules/photoUpload/services/photoUploadService';
 *
 * // Upload a profile photo
 * const photoUrl = await photoUploadService.uploadProfilePhoto(file, 'user-123');
 *
 * // Delete a profile photo
 * await photoUploadService.deleteProfilePhoto(photoUrl);
 * ```
 */
const photoUploadService = {
  /**
   * Upload a profile photo for a user.
   *
   * Generates a unique filename with timestamp and uploads the file to
   * Firebase Storage. The filename is sanitized to remove special characters.
   *
   * @param file - The image file to upload
   * @param userId - The user's unique identifier
   * @returns Promise resolving to the uploaded photo URL
   * @throws {Error} When upload fails
   *
   * @example
   * ```typescript
   * try {
   *   const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
   *   const photoUrl = await photoUploadService.uploadProfilePhoto(file, 'user-123');
   *   console.log('Photo uploaded:', photoUrl);
   * } catch (error) {
   *   console.error('Upload failed:', error);
   * }
   * ```
   */
  async uploadProfilePhoto(file: File, userId: string): Promise<string> {
    try {
      // Generate unique photo ID with timestamp
      const timestamp = Date.now();
      const photoId = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;

      const photoUrl = await withOperationTimeout(
        storage.uploadProfilePhoto(userId, file, photoId),
        UPLOAD_TIMEOUT_MS,
        UPLOAD_TIMEOUT_MESSAGE
      );

      logger.info("Profile photo uploaded successfully:", { userId, photoId });
      return photoUrl;
    } catch (error) {
      logger.error("Profile photo upload failed:", error);
      if (error instanceof Error && error.message === UPLOAD_TIMEOUT_MESSAGE) {
        throw error;
      }
      throw new Error("Failed to upload profile photo");
    }
  },

  /**
   * Delete a profile photo from storage.
   *
   * Extracts the filename from the photo URL and deletes the file
   * from Firebase Storage.
   *
   * @param photoUrl - The URL of the photo to delete
   * @returns Promise that resolves when deletion is complete
   * @throws {Error} When deletion fails or URL is invalid
   *
   * @example
   * ```typescript
   * try {
   *   await photoUploadService.deleteProfilePhoto('https://storage.googleapis.com/...');
   *   console.log('Photo deleted successfully');
   * } catch (error) {
   *   console.error('Delete failed:', error);
   * }
   * ```
   */
  async deleteProfilePhoto(photoUrl: string): Promise<void> {
    try {
      // Extract filename from URL
      const filename = extractFilenameFromUrl(photoUrl);
      await storage.deleteFile(filename);

      logger.info("Profile photo deleted successfully:", { filename });
    } catch (error) {
      logger.error("Profile photo deletion failed:", error);
      throw new Error("Failed to delete profile photo");
    }
  },
};

/**
 * Extracts the filename from a Firebase Storage URL.
 *
 * Parses the Storage URL to extract the file path, handling URL encoding
 * and query parameters. Used internally for file deletion operations.
 *
 * @param url - Full Firebase Storage URL
 * @returns Extracted filename with path prefix
 * @throws {Error} When URL is invalid or malformed
 *
 * @example
 * ```typescript
 * const url = 'https://storage.googleapis.com/bucket/o/path%2Fto%2Ffile.jpg?token=123';
 * const filename = extractFilenameFromUrl(url);
 * // Returns: 'path/to/file.jpg'
 * ```
 */
function extractFilenameFromUrl(url: string): string {
  // Extract the path from the URL (everything after /o/)
  const urlParts = url.split("/o/");
  if (urlParts.length < 2 || !urlParts[1]) {
    throw new Error("Invalid Storage URL");
  }

  // Get the path and remove query parameters
  const pathWithQuery = urlParts[1];
  const path = pathWithQuery.split("?")[0];

  if (!path) {
    throw new Error("Invalid Storage URL: no path found");
  }

  // Decode URL encoding (%2F becomes /)
  const decodedPath = decodeURIComponent(path);

  return decodedPath;
}

export default photoUploadService;
